import { useContext, useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import logo from '../../assets/ticketin-darkmode.svg';
import { LuChevronLeft, LuChevronRight, LuLogOut, LuHeart } from 'react-icons/lu';
import AppIcon from '../icons/app-icon';

import sidebarNav from '../../configs/sidebarNav';
import {
  GlobalStateContext,
  GlobalStateContextType,
} from '../../context/global-state-context';
import { api } from '../../api/interceptor';
import { clearAuthSession, getRefreshToken } from '../../auth/token-storage';
import './sidebar.scss';

const Sidebar = ({ toggleSidebar }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpenState, setIsSidebarOpenState] = useState(false);
  const { setUser } = useContext<GlobalStateContextType>(GlobalStateContext);
  const [iconDirection, setIconDirection] = useState(false);

  const handleSidebarClick = () => {
    setIconDirection(!iconDirection);
    toggleSidebar();
  };

  // Effect to set the active index based on the current location
  useEffect(() => {
    const curPath = window.location.pathname.split('/')[1];
    let activeItem;
    if (curPath.length === 0) {
      // If at root, highlight 'events' menu
      activeItem = sidebarNav.findIndex((item) => item.section === 'events');
    } else {
      activeItem = sidebarNav.findIndex((item) => item.section === curPath);
    }
    setActiveIndex(activeItem);
  }, [location]);

  const logout = async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }
    } catch {
      // Logout is idempotent server-side; storage must still be cleared locally.
    } finally {
      clearAuthSession();
      setUser({ loggedIn: false, userId: '', username: '' });
      navigate('/login');
    }
  };

  useEffect(() => {
    const isSidebarOpen = () => {
      const sidebar = document.querySelector('.sidebar-open');

      if (sidebar) {
        setIsSidebarOpenState(false);
      } else {
        setIsSidebarOpenState(true);
      }
    };

    const observer = new MutationObserver(isSidebarOpen);
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class'],
    });
  }, []);

  // const userPrivileges = JSON.parse(localStorage.getItem('user'))?.userOrgs[0]
  //   ?.privilege?.name;

  const isSuperAdmin = useMemo(() => {
    const user = localStorage.getItem('roles');
    if (!user) return false;
    try {
      const roles = JSON.parse(user);
      return roles.includes('superadmin');
    } catch {
      return false;
    }
  }, []);

  const isAdminOrSuperAdmin = useMemo(() => {
    const user = localStorage.getItem('roles');
    if (!user) return false;
    try {
      const roles = JSON.parse(user);
      return roles.includes('superadmin') || roles.includes('orgadmin');
    } catch {
      return false;
    }
  }, []);

  return (
    <Box
      className='sidebar'
      sx={{
        width: !isSidebarOpenState ? '240px' : '10px',
        padding: !isSidebarOpenState ? '30px 20px' : '0',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : theme.palette.text.primary,
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        className='sidebar-toggle'
        onClick={handleSidebarClick}
        sx={{
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.text.primary,
          color: theme.palette.common.white,
          border: `3px solid ${theme.palette.background.default}`,
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
          },
        }}
      >
        {!iconDirection ? (
          <AppIcon icon={LuChevronLeft} size={22} className='icon-sidebar' />
        ) : (
          <AppIcon icon={LuChevronRight} size={22} className='icon-sidebar' />
        )}
      </Box>
      <div
        className='sidebar__logo'
        style={{ display: !isSidebarOpenState ? '' : 'none' }}
      >
        <img style={{ width: '80px' }} src={logo} alt='' />
      </div>
      <div
        className='sidebar__menu'
        style={{ display: !isSidebarOpenState ? '' : 'none' }}
      >
        <div
          className='sidebar__menu__list'
          style={{ width: !isSidebarOpenState ? '100%' : 'auto' }}
        >
          {sidebarNav.map((nav, index) => {
            if (
              nav?.section === 'organization-members' ||
              (nav?.superAdminOnly && !isSuperAdmin) ||
              (nav?.section === 'organizations' && !isSuperAdmin) ||
              (nav?.section === 'guests' && !isAdminOrSuperAdmin)
            )
              return null;
            return (
              <Link
                to={nav.link}
                key={index}
                className={`sidebar__menu__list__item${
                  activeIndex === index ? ' active' : ''
                }${isSidebarOpenState ? ' hovered-item' : ''}`}
                style={{
                  justifyContent: !isSidebarOpenState ? 'flex-start' : 'center',
                  padding: !isSidebarOpenState ? '9px 16px' : '10px 13px',
                  backgroundColor:
                    activeIndex === index
                      ? theme.palette.primary.main
                      : undefined,
                  color:
                    activeIndex === index
                      ? theme.palette.primary.contrastText
                      : 'rgba(255, 255, 255, 0.78)',
                  fontWeight: activeIndex === index ? 800 : 600,
                  textDecoration: 'none',
                  transition: 'background-color 0.18s ease, color 0.18s ease',
                }}
              >
                <div className='sidebar__menu__list__item__icon'>
                  {nav.icon}
                </div>
                {!isSidebarOpenState && (
                  <div className='sidebar__menu__list__item__txt'>
                    {nav.text}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
        {/* <div onClick={() => logout()} className="logout">
          <div style={{ fontWeight: 400 }}>Logout</div>
          <div className="logout__icon">
            <LogoutRoundedIcon
              sx={{ color: "#FD523E", fontSize: "18px" }}
              className="bx bx-log-out-circle"
            />
          </div>
        </div> */}

        {!isSidebarOpenState && (
          <Box sx={{ px: 2, pt: 2, pb: 1, textAlign: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Box
                component='span'
                sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}
              >
                Built with passion
              </Box>
              <AppIcon icon={LuHeart} size={11} color='primary.main' />
            </Box>
            <Box
              sx={{
                fontSize: 12.5,
                mt: 0.5,
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 600,
              }}
            >
              by{' '}
              {/* Swap this wordmark for <Box component='img' src={qafLogo} sx={{ height: 13 }} /> once the QAF Lab logo asset is added. */}
              <Box
                component='span'
                sx={{ color: 'primary.main', fontWeight: 800 }}
              >
                QAF Lab
              </Box>{' '}
              Tech Team
            </Box>
          </Box>
        )}

        <Box
          onClick={() => logout()}
          className={`sidebar__menu__list__item`}
          style={{
            justifyContent: !isSidebarOpenState ? 'flex-start' : 'center',
            padding: !isSidebarOpenState ? '7px 20px' : '10px 13px',
            cursor: 'pointer',
          }}
        >
          <div className='sidebar__menu__list__item__icon'>
            <AppIcon icon={LuLogOut} size={21} color='error.main' />
          </div>
          {!isSidebarOpenState && (
            <Box
              className='sidebar__menu__list__item__txt'
              sx={{ color: 'rgba(255, 255, 255, 0.78)' }}
            >
              Logout
            </Box>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default Sidebar;
